import type { Theme, CSSObject, Components, ComponentsVariants } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import { iconButtonClasses } from '@mui/material/IconButton';

import { motion } from '../../motion';

// ----------------------------------------------------------------------

/**
 * TypeScript extension for MUI theme augmentation.
 * @to {@link file://./../../extend-theme-types.d.ts}
 *
 * Figma: DESIGN_SYSTEM_In-Progress → Icon Button (node 274:37817)
 *
 * Variants: primary, secondary, outline, ghost, ghostMuted, destructive
 * Sizes:    mini (24), small (32), medium (36, default), large (40)
 * Shapes:   contained (8px radius) and round (50%)
 */
export type IconButtonExtendColor = { black: true; white: true };
export type IconButtonExtendSize = { mini: true };

type IconButtonVariants = ComponentsVariants<Theme>['MuiIconButton'];

/* **********************************************************************
 * 📐 Sizes
 * **********************************************************************/
const SIZES: Record<'mini' | 'small' | 'medium' | 'large', CSSObject> = {
  mini: {
    width: 24,
    height: 24,
    padding: 4,
    '& svg': { width: 14, height: 14 },
  },
  small: {
    width: 32,
    height: 32,
    padding: 6,
    '& svg': { width: 16, height: 16 },
  },
  medium: {
    width: 36,
    height: 36,
    padding: 8,
    '& svg': { width: 20, height: 20 },
  },
  large: {
    width: 40,
    height: 40,
    padding: 10,
    '& svg': { width: 20, height: 20 },
  },
};

const sizeVariants = [
  { props: { size: 'mini' as const }, style: SIZES.mini },
  { props: { size: 'small' as const }, style: SIZES.small },
  { props: { size: 'medium' as const }, style: SIZES.medium },
  { props: { size: 'large' as const }, style: SIZES.large },
] satisfies IconButtonVariants;

/* **********************************************************************
 * 🎨 Variant styles
 * **********************************************************************/

/** Primary — dark filled background, white icon */
const containedVariants = [
  {
    props: (props) => props.variant === 'contained' && props.color === 'default',
    style: ({ theme }) => ({
      backgroundColor: theme.vars.palette.grey[800],
      color: theme.vars.palette.common.white,
      '&:hover': { backgroundColor: theme.vars.palette.container.high },
    }),
  },
  {
    props: (props) => props.variant === 'contained' && props.color === 'error',
    style: ({ theme }) => ({
      backgroundColor: theme.vars.palette.error.main,
      color: theme.vars.palette.error.contrastText,
      '&:hover': { backgroundColor: theme.vars.palette.error.dark },
    }),
  },
  {
    props: (props) => props.variant === 'contained' && props.color === 'secondary',
    style: ({ theme }) => ({
      backgroundColor: theme.vars.palette.container.medium,
      color: theme.vars.palette.grey[300],
      '&:hover': { backgroundColor: theme.vars.palette.container.high },
    }),
  },
] satisfies IconButtonVariants;

/** Outlined — border, transparent background */
const outlinedVariants = [
  {
    props: (props) => props.variant === 'outlined',
    style: ({ theme }) => ({
      backgroundColor: 'transparent',
      border: `1px solid ${varAlpha(theme.vars.palette.grey['600Channel'], 0.32)}`,
      color: theme.vars.palette.text.secondary,
      '&:hover': {
        backgroundColor: theme.vars.palette.action.hover,
        borderColor: theme.vars.palette.text.secondary,
      },
    }),
  },
  {
    props: (props) => props.variant === 'outlined' && props.color === 'error',
    style: ({ theme }) => ({
      borderColor: theme.vars.palette.error.main,
      color: theme.vars.palette.error.main,
      '&:hover': {
        backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
        borderColor: theme.vars.palette.error.dark,
      },
    }),
  },
] satisfies IconButtonVariants;

/** Ghost — no background, no border */
const ghostVariants = [
  {
    props: (props) => props.variant === 'ghost',
    style: ({ theme }) => ({
      backgroundColor: 'transparent',
      color: theme.vars.palette.text.secondary,
      '&:hover': { backgroundColor: theme.vars.palette.action.hover },
    }),
  },
  {
    props: (props) => props.variant === 'ghost' && props.color === 'error',
    style: ({ theme }) => ({
      color: theme.vars.palette.error.main,
      '&:hover': {
        backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
      },
    }),
  },
] satisfies IconButtonVariants;

/** Ghost Muted — like ghost but dimmer icon */
const ghostMutedVariants = [
  {
    props: (props) => props.variant === 'ghostMuted',
    style: ({ theme }) => ({
      backgroundColor: 'transparent',
      color: theme.vars.palette.grey[600],
      '&:hover': {
        backgroundColor: theme.vars.palette.action.hover,
        color: theme.vars.palette.text.secondary,
      },
    }),
  },
  {
    props: (props) => props.variant === 'ghostMuted' && props.color === 'error',
    style: ({ theme }) => ({
      color: varAlpha(theme.vars.palette.error.mainChannel, 0.5),
      '&:hover': {
        backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
        color: theme.vars.palette.error.main,
      },
    }),
  },
] satisfies IconButtonVariants;

/** Disabled */
const disabledVariants = [
  {
    props: (props) => props.variant === 'contained',
    style: ({ theme }) => ({
      [`&.${iconButtonClasses.disabled}`]: {
        opacity: 0.48,
        color: theme.vars.palette.text.disabled,
      },
    }),
  },
  {
    props: (props) => props.variant === 'outlined' || props.variant === 'ghost' || props.variant === 'ghostMuted',
    style: ({ theme }) => ({
      [`&.${iconButtonClasses.disabled}`]: {
        color: theme.vars.palette.text.disabled,
        borderColor: 'transparent',
      },
    }),
  },
] satisfies IconButtonVariants;

/* **********************************************************************
 * 🧩 Components
 * **********************************************************************/
const MuiIconButton: Components<Theme>['MuiIconButton'] = {
  defaultProps: {
    disableRipple: true,
    color: 'default',
    size: 'medium',
  },
  styleOverrides: {
    root: {
      borderRadius: 8,
      transition: `transform ${motion.duration.instant} ${motion.ease.out}, background-color ${motion.duration.fast} ${motion.ease.out}, color ${motion.duration.fast} ${motion.ease.out}, border-color ${motion.duration.fast} ${motion.ease.out}`,
      '&:active': { transform: 'scale(0.96)' },
      variants: [
        ...sizeVariants,
        ...containedVariants,
        ...outlinedVariants,
        ...ghostVariants,
        ...ghostMutedVariants,
        ...disabledVariants,
      ],
    },
  },
};

/* **********************************************************************
 * 🚀 Export
 * **********************************************************************/
export const iconButton: Components<Theme> = {
  MuiIconButton,
};
